import Image from "next/image";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
const CustomAccordion = dynamic(() => import("./capabilities-mobile"),{ssr: true});
import { toWebp } from "@/utils/common";

interface CapabilitiesProps {
  isMobile?: boolean;
  data?: any;
  slug?: any;
}

const Capabilities: React.FC<CapabilitiesProps> = ({ isMobile, data, slug  }) => {
  const [activeTab, setActiveTab] = useState(0);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";
  const [maxHeight, setMaxHeight] = useState(0);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate tallest content for sticky container
  useEffect(() => {
    if (!data?.Capability) return;
    let tallest = 0;
    data.Capability.forEach((cap: any, index: number) => {
      const el = contentRefs.current[index];
      if (el) {
        tallest = Math.max(tallest, el.scrollHeight);
      }
    });
    setMaxHeight(tallest);
  }, [data, slug]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (!data?.Capability) return;
      let tallest = 0;
      data.Capability.forEach((cap: any, index: number) => {
        const el = contentRefs.current[index];
        if (el) {
          tallest = Math.max(tallest, el.scrollHeight);
        }
      });
      setMaxHeight(tallest);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data]);

  return (
    <div className="relative sec-capabilities">
      <section className="xl:top-[-250px] md:top-[100px] bg-background-white text-primary-text py-[64px] xl:py-25 md:py-[80px] flex items-start">
        <div className="container mx-auto px-4 w-full relative z-10">
          {/* Heading */}
          <h2 className="text-heading-1 text-primary-text mx-auto font-bold text-center capitalize xl:max-w-[812px] max-w-[812px]">
            {data?.title}
          </h2>
          <p className="text-secondary-text text-subtitle-2 mx-auto text-center font-regular xl:max-w-[812px] max-w-[812px] mt-[12px] xl:mb-[40px] mb-[32px]">
            {data?.description}
          </p>

          {!isMobile ? (
            <div className="grid grid-cols-12 gap-4 justify-between relative">
              {/* Left: Navigation List */}
              <div className="col-span-12 md:col-span-6">
                <div className="space-y-8">
                  {data.Capability.map((cap: any, index: number) => {
                    const displayIndex = index + 1;
                    return (
                      <motion.div
                        key={cap?.id}
                        className={`border border-[#B0D9F7] 
                                    relative group min-h-[48px] 
                                    flex xl:flex md:inline-flex w-[90%] xl:w-[475px]
                                    items-center text-subtitle-1 
                                    font-bold px-[12px] py-[16px] cursor-pointer
                                    transition-all duration-200
                                    ${
                                      activeTab === index
                                        ? "border-l-[10px] border-l-[#B0D9F7]-500 bg-[#B0D9F7]-50"
                                        : ""
                                    }`}
                        onClick={() => setActiveTab(index)}
                      >
                        <span
                          className={`font-bold group-hover:text-[#040405] ${
                            activeTab === index
                              ? "text-[#040405]"
                              : "text-[#83858C]"
                          }`}
                        >
                          {displayIndex}.
                        </span>
                        <span
                          className={`ml-2 block group-hover:text-[#040405] ${
                            activeTab === index
                              ? "text-[#040405]"
                              : "text-[#83858C]"
                          }`}
                        >
                          {cap?.short_title}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Sticky Content Area */}
              <div className="col-span-12 md:col-span-6">
                <div
                  className="sticky top-[100px] -webkit-sticky bg-white relative "
                  style={{ height: `${maxHeight}px` }} // fixed height = tallest content
                >
                  {data.Capability.map((cap: any, index: number) => (
                    <motion.div
                      key={cap.id}
                      ref={(el) => {
                        if (el) {
                          contentRefs.current[index] = el;
                        }
                      }}
                      className="absolute top-0 left-0 w-full p-6 border border-[#B0D9F7]"
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: activeTab === index ? 1 : 0,
                        pointerEvents: activeTab === index ? "auto" : "none",
                      }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                    >
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">
                        {cap.title}
                      </h3>

                      {cap?.image?.data?.attributes?.url && (
                        <div className="relative w-full h-[287px] overflow-hidden">
                          <Image
                            src={toWebp(
                              `${baseUrl}${cap.image.data.attributes.url}`,
                            )}
                            fill
                            quality={100}
                            className="object-cover transition-all duration-500 ease-in-out"
                            alt={cap.title || ""}
                            loading="lazy"
                          />
                        </div>
                      )}

                      {cap.description && (
                        <p className="mt-4 text-gray-700">{cap.description}</p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <CustomAccordion data={data} />
          )}
        </div>
      </section>
    </div>
  );
}

export default Capabilities;
